// src/pages/admin/Transactions.jsx - Revamped with real data integration
import React, { useState, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import TransactionDetailsModal from '../../components/transactions/TransactionDetailsModal';
import PaymentRequestDetailsModal from '../../components/payments/PaymentRequestDetailsModal';
import {
  Download,
  Filter,
  RefreshCw,
  Search,
  Eye,
  CheckCircle,
  AlertCircle,
  Clock,
  Play,
  CreditCard,
  QrCode,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Package,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown
} from 'lucide-react';
import { GET_TRANSACTIONS_PAGINATED, GET_TRANSACTION_STATISTICS } from '../../api/transactions-api';
import { GET_PAYMENT_REQUEST } from '../../api/payment-requests-api';
import { exportToExcel } from '../../utils/exportToExcel';
import { GET_LABORATORIES } from '../../api/laboratory-api';

const Transactions = () => {
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
    productName: '',
    laboratoryId: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isExporting, setIsExporting] = useState(false);
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  const [selectedPaymentRequest, setSelectedPaymentRequest] = useState(null);
  const [showPaymentRequestModal, setShowPaymentRequestModal] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.searchTerm]);

  // Server-side paginated query
  const queryVariables = {
    page: currentPage,
    limit: pageSize,
    status: filters.status || undefined,
    type: filters.type || undefined,
    search: debouncedSearch || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    sortBy,
    sortOrder,
    productName: filters.productName || undefined,
    laboratoryId: filters.laboratoryId || undefined,
  };

  const {
    loading,
    error,
    data: transactionsData,
    refetch: refetchTransactions
  } = useQuery(GET_TRANSACTIONS_PAGINATED, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  const { data: statsData } = useQuery(GET_TRANSACTION_STATISTICS, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: laboratoriesData } = useQuery(GET_LABORATORIES, {
    fetchPolicy: 'cache-and-network',
  });
  const laboratories = laboratoriesData?.laboratories || [];

  const [fetchAllForExport] = useLazyQuery(GET_TRANSACTIONS_PAGINATED, {
    fetchPolicy: 'network-only',
  });

  const [fetchPaymentRequest] = useLazyQuery(GET_PAYMENT_REQUEST, {
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      if (data?.paymentRequest) {
        setSelectedPaymentRequest(data.paymentRequest);
        setShowPaymentRequestModal(true);
      }
    },
  });

  const handleViewPaymentRequest = (e, paymentRequestId) => {
    e.stopPropagation();
    fetchPaymentRequest({ variables: { id: paymentRequestId } });
  };

  const transactions = transactionsData?.transactionsPaginated?.transactions || [];
  const totalCount = transactionsData?.transactionsPaginated?.total ?? 0;
  const totalPages = transactionsData?.transactionsPaginated?.totalPages ?? 1;

  const rawStats = statsData?.transactionStatistics;
  const globalStats = {
    total: rawStats?.total ?? 0,
    totalAmount: rawStats?.totalGains ?? 0,
    totalCommission: rawStats?.totalCommissions ?? 0,
    scans: rawStats?.scans ?? 0,
    withdrawals: rawStats?.withdrawals ?? 0,
    pending: rawStats?.pending ?? 0,
    validated: rawStats?.validated ?? 0,
    thisMonth: rawStats?.thisMonth ?? 0,
  };

  const hasActiveFilters = !!(filters.status || filters.type || filters.searchTerm || filters.dateFrom || filters.dateTo || filters.productName || filters.laboratoryId || showDuplicatesOnly);

  // When filters are active, derive stats from the filtered result set
  // totalCount is the accurate filtered total; other values computed from the current page
  const filteredStats = {
    total: totalCount,
    totalAmount: transactions.reduce((sum, t) => sum + (t.gain || 0), 0),
    totalCommission: transactions.reduce((sum, t) => sum + (t.companyCommission || 0), 0),
    scans: transactions.filter(t => t.type === 'SCAN').length,
    withdrawals: transactions.filter(t => t.type === 'WITHDRAWAL').length,
    pending: transactions.filter(t => t.status === 'PENDING').length,
    validated: transactions.filter(t => t.status === 'VALIDATED').length,
    thisMonth: globalStats.thisMonth,
  };

  const stats = hasActiveFilters ? filteredStats : globalStats;

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    if (name !== 'searchTerm') setCurrentPage(1);
  };

  // Count QR codes on the current page to detect duplicates
  const qrCodePageCounts = transactions.reduce((acc, t) => {
    if (t.type === 'SCAN' && t.label?.code) acc[t.label.code] = (acc[t.label.code] || 0) + 1;
    return acc;
  }, {});

  // Apply client-side duplicate filter on top of the server-side page
  const paginatedTransactions = showDuplicatesOnly
    ? transactions.filter(t => t.type === 'SCAN' && t.label?.code && qrCodePageCounts[t.label.code] > 1)
    : transactions;

  // Status options
  const statusOptions = [
    { value: '', label: 'Tous les statuts' },
    { value: 'IN_PROGRESS', label: 'En cours' },
    { value: 'PENDING', label: 'En attente' },
    { value: 'VALIDATED', label: 'Validée' },
    { value: 'CANCELLED', label: 'Annulée' },
  ];

  // Transaction types
  const typeOptions = [
    { value: '', label: 'Tous les types' },
    { value: 'SCAN', label: 'Scan QR' },
    { value: 'WITHDRAWAL', label: 'Retrait' },
  ];

  // Function to get status style
  const getStatusStyle = (status) => {
    switch (status) {
      case 'VALIDATED':
        return { 
          bgColor: 'bg-green-100', 
          textColor: 'text-green-800',
          icon: <CheckCircle size={14} className="mr-1 text-green-600" /> 
        };
      case 'PENDING':
        return { 
          bgColor: 'bg-yellow-100', 
          textColor: 'text-yellow-800',
          icon: <Clock size={14} className="mr-1 text-yellow-600" /> 
        };
      case 'IN_PROGRESS':
        return { 
          bgColor: 'bg-blue-100', 
          textColor: 'text-blue-800',
          icon: <Play size={14} className="mr-1 text-blue-600" /> 
        };
      case 'CANCELLED':
        return { 
          bgColor: 'bg-red-100', 
          textColor: 'text-red-800',
          icon: <AlertCircle size={14} className="mr-1 text-red-600" /> 
        };
      default:
        return { 
          bgColor: 'bg-gray-100', 
          textColor: 'text-gray-800',
          icon: null 
        };
    }
  };

  // Get label for status
  const getStatusLabel = (status) => {
    switch (status) {
      case 'VALIDATED': return 'Validée';
      case 'PENDING': return 'En attente';
      case 'IN_PROGRESS': return 'En cours';
      case 'CANCELLED': return 'Annulée';
      default: return status;
    }
  };

  // Get label for type
  const getTypeLabel = (type) => {
    switch (type) {
      case 'SCAN': return 'Scan QR';
      case 'WITHDRAWAL': return 'Retrait';
      default: return type;
    }
  };

  // Function to refresh data
  const handleRefresh = () => {
    refetchTransactions();
    toast.success('Données actualisées');
  };

  // Function to view transaction details
  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };

  const buildExcelAndDownload = (allTransactions) => {
    const headers = [
      'ID', 'Date', 'Type', 'Statut', 'Utilisateur', 'Email',
      'Produit', 'Gain (DH)', 'Commission Mazid (DH)', 'Pharmacie', 'Code QR'
    ];
    const rows = allTransactions.map(transaction => [
      transaction.id,
      format(new Date(transaction.date), 'dd/MM/yyyy HH:mm'),
      getTypeLabel(transaction.type),
      getStatusLabel(transaction.status),
      `${transaction.user?.firstName || ''} ${transaction.user?.lastName || ''}`.trim(),
      transaction.user?.email || '',
      transaction.product?.name || '',
      transaction.gain.toFixed(2),
      transaction.companyCommission.toFixed(2),
      transaction.pharmacy?.namePharmacy || '',
      transaction.label?.code || '',
    ]);
    exportToExcel(headers, rows, `transactions_${format(new Date(), 'yyyy-MM-dd')}.xls`);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { data } = await fetchAllForExport({
        variables: {
          page: 1,
          limit: totalCount || 10000,
          status: filters.status || undefined,
          type: filters.type || undefined,
          search: debouncedSearch || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          sortBy,
          sortOrder,
          productName: filters.productName || undefined,
          laboratoryId: filters.laboratoryId || undefined,
        },
      });
      const all = data?.transactionsPaginated?.transactions || [];
      buildExcelAndDownload(all);
      toast.success(`Export terminé — ${all.length} transactions`);
    } catch (err) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setIsExporting(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ status: '', type: '', searchTerm: '', dateFrom: '', dateTo: '', productName: '', laboratoryId: '' });
    setShowDuplicatesOnly(false);
    setCurrentPage(1);
  };

  // Handle column sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ChevronsUpDown size={14} className="ml-1 text-gray-300 inline" />;
    return sortOrder === 'asc'
      ? <ChevronUp size={14} className="ml-1 text-blue-500 inline" />
      : <ChevronDown size={14} className="ml-1 text-blue-500 inline" />;
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Erreur!</strong>
          <span className="block sm:inline"> Impossible de charger les transactions: {error.message}</span>
          <button 
            onClick={handleRefresh}
            className="mt-3 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-600 text-white p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Transactions</h1>
            <p className="text-blue-100 mt-1 text-sm hidden sm:block">
              Gestion et suivi des transactions de la plateforme
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center px-3 py-2 text-sm bg-white/20 backdrop-blur rounded-md hover:bg-white/30 transition-colors"
            >
              <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center px-3 py-2 text-sm bg-white/20 backdrop-blur rounded-md hover:bg-white/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Download size={14} className={`mr-1.5 ${isExporting ? 'animate-bounce' : ''}`} />
              <span className="hidden sm:inline">{isExporting ? 'Export...' : 'Exporter'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics cards */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 px-1">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Statistiques filtrées · page courante
          </span>
          <span className="text-xs text-gray-500 hidden sm:inline">Les totaux reflètent les filtres actifs</span>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Transactions</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-sm text-green-600">{stats.thisMonth} ce mois</span>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <CreditCard size={24} className="text-blue-500" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">Montant Total</p>
              <p className="text-3xl font-bold text-gray-900">
                {stats.totalAmount.toLocaleString('fr-FR', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })} DH
              </p>
              <div className="flex items-center mt-2">
                <DollarSign className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-sm text-green-600">
                  Commission: {stats.totalCommission.toFixed(2)} DH
                </span>
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <TrendingUp size={24} className="text-green-500" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">Scans QR</p>
              <p className="text-3xl font-bold text-gray-900">{stats.scans.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <span className="text-sm text-purple-600">
                  {stats.withdrawals} retraits
                </span>
              </div>
            </div>
            <div className="p-3 bg-purple-50 rounded-full">
              <QrCode size={24} className="text-purple-500" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">En attente</p>
              <p className="text-3xl font-bold text-gray-900">{stats.pending.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-sm text-green-600">{stats.validated} validées</span>
              </div>
            </div>
            <div className="p-3 bg-yellow-50 rounded-full">
              <Clock size={24} className="text-yellow-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filter section */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Filter className="mr-2 h-5 w-5" />
            Filtres avancés
          </h3>
          <button
            onClick={clearFilters}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Réinitialiser
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Rechercher
            </label>
            <div className="relative">
              <input
                type="text"
                id="search"
                name="searchTerm"
                value={filters.searchTerm}
                onChange={handleFilterChange}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="ID, utilisateur, produit, QR..."
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Type */}
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              id="type"
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {typeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Page Size */}
          <div>
            <label htmlFor="pageSize" className="block text-sm font-medium text-gray-700 mb-1">
              Par page
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {[10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700 mb-1">
              Du
            </label>
            <input
              type="date"
              id="dateFrom"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {/* Date To */}
          <div>
            <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700 mb-1">
              Au
            </label>
            <input
              type="date"
              id="dateTo"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {/* Product Name */}
          <div>
            <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">
              Produit
            </label>
            <div className="relative">
              <input
                type="text"
                id="productName"
                name="productName"
                value={filters.productName}
                onChange={handleFilterChange}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Nom du produit..."
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Package size={16} className="text-gray-400" />
              </div>
            </div>
          </div>

          {/* Laboratory */}
          <div>
            <label htmlFor="laboratoryId" className="block text-sm font-medium text-gray-700 mb-1">
              Laboratoire
            </label>
            <select
              id="laboratoryId"
              name="laboratoryId"
              value={filters.laboratoryId}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Tous les laboratoires</option>
              {laboratories.map(lab => (
                <option key={lab.id} value={lab.id}>
                  {lab.nom || lab.raisonSociale}
                </option>
              ))}
            </select>
          </div>

          {/* Duplicates only */}
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showDuplicatesOnly}
                onChange={(e) => { setShowDuplicatesOnly(e.target.checked); setCurrentPage(1); }}
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <AlertCircle size={14} className="text-red-500" />
                Doublons uniquement
              </span>
            </label>
          </div>
        </div>
        
        {/* Active filters display */}
        {(filters.status || filters.type || filters.searchTerm || filters.dateFrom || filters.dateTo || filters.productName || filters.laboratoryId || showDuplicatesOnly) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Filtres actifs:</span>
            
            {filters.status && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Statut: {statusOptions.find(o => o.value === filters.status)?.label}
                <button
                  type="button"
                  className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none"
                  onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
                >
                  ×
                </button>
              </span>
            )}

            {filters.type && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Type: {typeOptions.find(o => o.value === filters.type)?.label}
                <button
                  type="button"
                  className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none"
                  onClick={() => setFilters(prev => ({ ...prev, type: '' }))}
                >
                  ×
                </button>
              </span>
            )}

            {filters.searchTerm && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Recherche: "{filters.searchTerm}"
                <button
                  type="button"
                  className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none"
                  onClick={() => setFilters(prev => ({ ...prev, searchTerm: '' }))}
                >
                  ×
                </button>
              </span>
            )}

            {(filters.dateFrom || filters.dateTo) && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Période: {filters.dateFrom || '...'} - {filters.dateTo || '...'}
                <button
                  type="button"
                  className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none"
                  onClick={() => setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }))}
                >
                  ×
                </button>
              </span>
            )}

            {filters.productName && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Produit: "{filters.productName}"
                <button
                  type="button"
                  className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none"
                  onClick={() => setFilters(prev => ({ ...prev, productName: '' }))}
                >
                  ×
                </button>
              </span>
            )}

            {filters.laboratoryId && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Labo: {laboratories.find(l => l.id === filters.laboratoryId)?.nom || filters.laboratoryId}
                <button
                  type="button"
                  className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none"
                  onClick={() => setFilters(prev => ({ ...prev, laboratoryId: '' }))}
                >
                  ×
                </button>
              </span>
            )}

            {showDuplicatesOnly && (
              <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Doublons uniquement
                <button
                  type="button"
                  className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-red-400 hover:bg-red-200 hover:text-red-500 focus:outline-none"
                  onClick={() => setShowDuplicatesOnly(false)}
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center p-12">
          <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm rounded-md text-white bg-blue-700 transition ease-in-out duration-150">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Chargement des transactions...
          </div>
        </div>
      )}

      {/* Transactions table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">
                Liste des transactions
                {totalCount > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({totalCount} résultats)
                  </span>
                )}
              </h2>
              <div className="text-sm text-gray-500">
                Page {currentPage} sur {totalPages}
              </div>
            </div>
          </div>

          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">Aucune transaction trouvée</h3>
              <p className="mt-1 text-gray-500">
                {filters.searchTerm || filters.status || filters.type || filters.dateFrom || filters.dateTo ?
                  "Aucune transaction ne correspond à vos critères de recherche." :
                  "Aucune transaction disponible pour le moment."}
              </p>
              {(filters.searchTerm || filters.status || filters.type || filters.dateFrom || filters.dateTo) && (
                <div className="mt-6">
                  <button
                    onClick={clearFilters}
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
                        onClick={() => handleSort('date')}
                      >
                        Date<SortIcon column="date" />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utilisateur
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Produit / QR Code
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pharmacie
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Région / Ville
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
                        onClick={() => handleSort('status')}
                      >
                        Statut<SortIcon column="status" />
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100"
                        onClick={() => handleSort('gain')}
                      >
                        Montant<SortIcon column="gain" />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Demande paiement
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedTransactions.map((transaction) => {
                      const statusStyle = getStatusStyle(transaction.status);
                      const isDuplicateScan = transaction.type === 'SCAN'
                        && transaction.label?.code
                        && qrCodePageCounts[transaction.label.code] > 1;

                      return (
                        <tr key={transaction.id} className={`transition-colors cursor-pointer ${isDuplicateScan ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`} onClick={() => handleViewTransaction(transaction)}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {format(new Date(transaction.date), 'dd MMM yyyy', { locale: fr })}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {format(new Date(transaction.date), 'HH:mm', { locale: fr })}
                                </div>
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-4 py-4 whitespace-nowrap">
                            {transaction.user ? (
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8">
                                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                    <Users className="h-4 w-4 text-gray-500" />
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <div className="text-sm font-medium text-gray-900">
                                    {transaction.user.firstName} {transaction.user.lastName}
                                  </div>
                                  <div className="text-xs text-gray-500">{transaction.user.email}</div>
                                  <div className="text-xs text-blue-600">{transaction.user.userType}</div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">Utilisateur non défini</span>
                            )}
                          </td>
                          
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              {transaction.product ? (
                                <div className="flex items-center">
                                  <Package className="h-4 w-4 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {transaction.product.name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Prix: {transaction.product.price?.toFixed(2)} DH
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-500">Aucun produit</span>
                              )}
                              
                              {transaction.label && (
                                <div className="flex items-center mt-1 gap-1">
                                  <QrCode className="h-3 w-3 text-gray-400" />
                                  <span className="text-xs font-mono text-gray-600">
                                    {transaction.label.code}
                                  </span>
                                  {isDuplicateScan && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                      Doublon
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          
                          <td className="px-4 py-4 whitespace-nowrap">
                            {transaction.pharmacy ? (
                              <div>
                                <div className="text-sm font-medium text-gray-900">{transaction.pharmacy.namePharmacy}</div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            {transaction.pharmacy ? (
                              <div className="space-y-0.5">
                                {transaction.pharmacy.region && (
                                  <div className="text-xs font-medium text-gray-700">{transaction.pharmacy.region.nomRegion}</div>
                                )}
                                {transaction.pharmacy.ville && (
                                  <div className="text-xs text-gray-500">{transaction.pharmacy.ville.nomVilleVillage}</div>
                                )}
                                {!transaction.pharmacy.region && !transaction.pharmacy.ville && (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            {(() => {
                              const s = getStatusStyle(transaction.status);
                              return (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${s.bgColor} ${s.textColor}`}>
                                  {s.icon}
                                  {getStatusLabel(transaction.status)}
                                </span>
                              );
                            })()}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-green-600">
                              {transaction.gain.toFixed(2)} DH
                            </div>
                            <div className="text-xs text-gray-500">
                              Commission: {transaction.companyCommission.toFixed(2)}%
                            </div>
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            {transaction.paymentRequestId ? (
                              <button
                                onClick={(e) => handleViewPaymentRequest(e, transaction.paymentRequestId)}
                                className="inline-flex items-center px-2 py-1 rounded text-xs font-mono font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors"
                                title={`Voir la demande de paiement ${transaction.paymentRequestId}`}
                              >
                                #{transaction.paymentRequestId.slice(0, 8)}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>

                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === 1 
                          ? 'text-gray-300 cursor-not-allowed bg-gray-100' 
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        currentPage === totalPages 
                          ? 'text-gray-300 cursor-not-allowed bg-gray-100' 
                          : 'text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      Suivant
                    </button>
                  </div>
                  
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Affichage de{' '}
                        <span className="font-medium">
                          {totalCount === 0 ? 0 : Math.min((currentPage - 1) * pageSize + 1, totalCount)}
                        </span>{' '}
                        à{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * pageSize, totalCount)}
                        </span>{' '}
                        sur{' '}
                        <span className="font-medium">{totalCount}</span> résultats
                      </p>
                    </div>
                    
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === 1 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {/* Generate page buttons */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              aria-current={currentPage === pageNum ? 'page' : undefined}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNum
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === totalPages 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Transaction details modal */}
      {selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTransaction(null);
          }}
          onRefresh={handleRefresh}
        />
      )}

      {/* Payment request details modal */}
      {selectedPaymentRequest && (
        <PaymentRequestDetailsModal
          request={selectedPaymentRequest}
          isOpen={showPaymentRequestModal}
          onClose={() => {
            setShowPaymentRequestModal(false);
            setSelectedPaymentRequest(null);
          }}
          onRefresh={() => {}}
        />
      )}
    </div>
  );
};

export default Transactions;